import { tenantApi } from '@shared/api/tenantApi';
import { unwrap, unwrapList } from '@shared/api/unwrap';

/**
 * Checkout — canonical member-facing routes. There is no `/me/checkout/*`
 * endpoint set; a checkout materialises the client-side cart into an order
 * via `POST /public/orders`, and saved payment methods come from
 * `GET /public/me/payment-methods`.
 */

export interface PaymentMethod {
  method_id: string;
  label: string;
  description?: string;
  type: 'card' | 'netcash' | 'paynow' | 'eft' | 'other';
  requires_redirect: boolean;
}

export interface CheckoutSession {
  session_id: string;
  cart_total: number;
  currency: string;
  redirect_url?: string;
  order_id?: string;
}

export async function fetchPaymentMethods(): Promise<PaymentMethod[]> {
  const { data } = await tenantApi.get('/public/me/payment-methods');
  return unwrapList<PaymentMethod>(data);
}

/**
 * Create the order from the cart. `payload.items` should carry the
 * client-side cart lines; the server returns the created order and an
 * optional hosted-payment redirect.
 */
export async function startCheckout(payload: {
  method_id?: string;
  return_url: string;
  items?: Array<{ product_id: string; variant_id?: string; quantity: number }>;
}): Promise<CheckoutSession> {
  const { data } = await tenantApi.post('/public/orders', payload);
  const order = unwrap<{
    order_id: string;
    total?: number;
    currency?: string;
    payment_url?: string;
    redirect_url?: string;
  }>(data);
  return {
    session_id: order.order_id,
    order_id: order.order_id,
    cart_total: order.total ?? 0,
    currency: order.currency ?? 'ZAR',
    redirect_url: order.payment_url ?? order.redirect_url,
  };
}

export async function pollCheckoutStatus(
  orderId: string,
): Promise<{ status: 'pending' | 'paid' | 'failed' | 'cancelled'; order_id?: string }> {
  const { data } = await tenantApi.get(`/public/orders/${orderId}`);
  const order = unwrap<{ status?: string }>(data);
  const status = (order.status ?? 'pending').toLowerCase();
  const mapped: 'pending' | 'paid' | 'failed' | 'cancelled' =
    status === 'paid' || status === 'fulfilled'
      ? 'paid'
      : status === 'cancelled'
        ? 'cancelled'
        : status === 'failed'
          ? 'failed'
          : 'pending';
  return { status: mapped, order_id: orderId };
}
